/**
 * Waiting out a rate limit, for every gateway call.
 *
 * The gateway limits each user per fixed 60 s window and answers a refused
 * call with 429, a `Retry-After` header and `retryAfterSeconds` in the body.
 * An upstream provider's 429 (Azure) passes through with its own
 * `Retry-After`. A call waits what it is told, capped at one window, plus
 * jitter so parallel calls do not return in lockstep, and keeps trying until
 * RATE_LIMIT_PATIENCE_MS has passed: more than one full window, so a call
 * always gets a try in a window it did not start in. After that the 429
 * reaches the caller. A rate limit inside a hosted turn is the gateway's to
 * wait out; the turn pauses on it (AgentService.stopNotice).
 */

/** The longest single wait: one gateway window. */
export const RATE_LIMIT_MAX_WAIT_MS = 60_000;
/** How long a call keeps retrying a 429 before giving it to the caller. */
export const RATE_LIMIT_PATIENCE_MS = 120_000;
const FALLBACK_WAIT_MS = 2_000;
const JITTER_MS = 1_000;

/** Seconds, or an HTTP date, as milliseconds from now; null when absent or unreadable. */
export function parseRetryAfter(value: string | null | undefined, now = Date.now()): number | null {
    if (!value) return null;
    const seconds = Number(value);
    if (Number.isFinite(seconds)) return seconds >= 0 ? seconds * 1000 : null;
    const date = Date.parse(value);
    return Number.isNaN(date) ? null : Math.max(0, date - now);
}

/** The wait before retry `attempt` (0-based): what the server asked, or doubling from 2 s, capped, plus jitter. */
export function rateLimitDelayMs(retryAfterMs: number | null, attempt: number, random: () => number = Math.random): number {
    const base = retryAfterMs ?? FALLBACK_WAIT_MS * 2 ** attempt;
    return Math.min(base, RATE_LIMIT_MAX_WAIT_MS) + Math.round(random() * JITTER_MS);
}

/** Sleep `ms`; rejects with the signal's reason on abort. */
export function waitForRateLimit(ms: number, signal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) { reject(signal.reason as Error); return; }
        const onAbort = () => { clearTimeout(timer); reject(signal?.reason as Error); };
        const timer = setTimeout(() => { signal?.removeEventListener('abort', onAbort); resolve(); }, ms);
        signal?.addEventListener('abort', onAbort, { once: true });
    });
}

/**
 * Run `call` until it is not rate limited or patience runs out. `retryAfter`
 * classifies an outcome: undefined when it is not a rate limit, otherwise the
 * server's wait in ms, or null when it named none. The last outcome is
 * returned or thrown as it came.
 */
export async function whileRateLimited<T>(
    call: () => Promise<T>,
    retryAfter: (outcome: { value: T } | { error: unknown }) => number | null | undefined,
    signal?: AbortSignal,
): Promise<T> {
    const started = Date.now();
    for (let attempt = 0; ; attempt++) {
        let outcome: { value: T } | { error: unknown };
        try { outcome = { value: await call() }; } catch (error) { outcome = { error }; }
        const after = retryAfter(outcome);
        if (after === undefined || Date.now() - started >= RATE_LIMIT_PATIENCE_MS) {
            if ('error' in outcome) throw outcome.error;
            return outcome.value;
        }
        const delay = rateLimitDelayMs(after, attempt);
        console.warn(`[AI] Rate limited, waiting ${Math.round(delay / 1000)} s (attempt ${attempt + 1})`);
        if ('value' in outcome && outcome.value instanceof Response) void outcome.value.body?.cancel().catch(() => {});
        await waitForRateLimit(delay, signal);
    }
}
